/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";
import { toast, ToastContainer } from "react-toastify";

import { motion, AnimatePresence } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import SummernoteEditor from "../createChapter/SummernoteEditor";

interface SectionData {
  sectionTitle: string;
  content: string;
}

interface ChapterData {
  chapterSlug: string;
  chapterSlugManual?: boolean;
  title: string;
  content: string;
  author: string;
  seoTitle: string;
  seoDescription: string;
  seoTags: string;
  seoHeading: string;
  seoHeadingManual?: boolean;
  canonicalUrl: string;
  createdAt: string;
  updatedAt?: string;
  sections?: SectionData[];
}

export default function UpdateChapterPage() {
  const { librarySlug, bookSlug, chapterSlug } = useParams();

  const [chapterData, setChapterData] = useState<ChapterData>({
    chapterSlug: "",
    title: "",
    content: "",
    author: "",
    seoTitle: "",
    seoDescription: "",
    seoTags: "",
    seoHeading: "",
    canonicalUrl: "",
    createdAt: new Date().toISOString(),
    sections: [],
  });

  const [, setShowPopup] = useState(false); // NEW

  // Load chapter
  useEffect(() => {
    if (!librarySlug || !bookSlug || !chapterSlug) return;

    const fetchChapter = async () => {
      try {
        const chapterRef = doc(
          fireDB,
          "NewBlogs",
          librarySlug,
          "books",
          bookSlug,
          "chapters",
          chapterSlug
        );

        const snap = await getDoc(chapterRef);
        if (snap.exists()) {
          setChapterData(snap.data() as ChapterData);
        } else {
          toast.error("Chapter not found");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching chapter");
      }
    };

    fetchChapter();
  }, [librarySlug, bookSlug, chapterSlug]);

  const addSection = () => {
    setChapterData((prev) => ({
      ...prev,
      sections: [...(prev.sections || []), { sectionTitle: "", content: "" }],
    }));
  };

  const updateSection = (index: number, key: keyof SectionData, value: string) => {
    setChapterData((prev) => {
      const updatedSections = [...(prev.sections || [])];
      updatedSections[index] = { ...updatedSections[index], [key]: value };
      return { ...prev, sections: updatedSections };
    });
  };

  const removeSection = (index: number) => {
    setChapterData((prev) => ({
      ...prev,
      sections: prev.sections?.filter((_, i) => i !== index),
    }));
  };

  // ===========================
  // UPDATE HANDLER + RENAME + MODAL
  // ===========================
  const handleUpdate = async () => {
    if (!librarySlug || !bookSlug || !chapterSlug) {
      toast.error("Missing identifiers");
      return;
    }

    try {
      const oldRef = doc(
        fireDB,
        "NewBlogs",
        librarySlug,
        "books",
        bookSlug,
        "chapters",
        chapterSlug
      );

      const newRef = doc(
        fireDB,
        "NewBlogs",
        librarySlug,
        "books",
        bookSlug,
        "chapters",
        chapterData.chapterSlug
      );

      // If slug changed → rename document
      if (chapterSlug !== chapterData.chapterSlug) {
        const updatedData = {
          ...chapterData,
          chapterSlug: chapterData.chapterSlug,
          updatedAt: Timestamp.now(),
        };

        await setDoc(newRef, updatedData);
        await deleteDoc(oldRef);

        toast.success("Slug & chapter updated successfully!");
        setShowPopup(true); // SHOW POPUP
        return;
      }

      // Normal update
      await updateDoc(oldRef, {
        title: chapterData.title,
        chapterSlug: chapterData.chapterSlug,
        author: chapterData.author,
        seoTitle: chapterData.seoTitle,
        seoDescription: chapterData.seoDescription,
        seoTags: chapterData.seoTags,
        seoHeading: chapterData.seoHeading,
        canonicalUrl: chapterData.canonicalUrl,
        sections: chapterData.sections,
        updatedAt: Timestamp.now(),
      });

      toast.success("Chapter updated!");
      setShowPopup(true); // SHOW POPUP

    } catch (err) {
      console.error(err);
      toast.error("Error updating chapter");
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen py-10 px-6">
      <div className="max-w-5xl mx-auto space-y-8">

        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-center mb-6 text-gray-800"
        >
          Update Chapter
        </motion.h1>

        {/* TITLE / SLUG / AUTHOR */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-red-100 to-blue-100 p-6 rounded-2xl shadow-lg flex flex-col md:flex-row gap-6"
        >
          <div className="flex-1 space-y-3">

            <label className="block font-semibold">Title</label>
            <input
              value={chapterData.title}
              onChange={(e) => setChapterData({ ...chapterData, title: e.target.value })}
              className="w-full border p-3 rounded-lg"
            />

            <label className="block font-semibold">Chapter Slug</label>
            <input
              value={chapterData.chapterSlug}
              onChange={(e) =>
                setChapterData({ ...chapterData, chapterSlug: e.target.value, chapterSlugManual: true })
              }
              className="w-full border p-3 rounded-lg"
            />

            <label className="block font-semibold">Author</label>
            <input
              value={chapterData.author}
              onChange={(e) => setChapterData({ ...chapterData, author: e.target.value })}
              className="w-full border p-3 rounded-lg"
            />
          </div>
        </motion.div>

        {/* SECTIONS */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-yellow-50 p-6 rounded-2xl shadow space-y-6"
        >
          <h2 className="text-3xl font-semibold mb-4 text-yellow-800">Sections</h2>

          <AnimatePresence>
            {(chapterData.sections || []).map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="bg-white p-5 rounded-2xl shadow border space-y-4"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Section {index + 1}</h3>
                  <button onClick={() => removeSection(index)} className="text-red-500 hover:text-red-700">
                    ❌ Remove
                  </button>
                </div>

                <input
                  placeholder="Section Title"
                  value={section.sectionTitle}
                  onChange={(e) => updateSection(index, "sectionTitle", e.target.value)}
                  className="w-full border p-3 rounded-lg"
                />

                <SummernoteEditor
                  editorId={`section-${index}`}
                  initialContent={section.content}
                  onChange={(content) => updateSection(index, "content", content)}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.03 }}
            onClick={addSection}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl shadow hover:bg-blue-700 transition-all"
          >
            ➕ Add Section
          </motion.button>
        </motion.div>

        {/* SEO */}
        <div className="bg-gray-200 p-6 rounded-2xl shadow space-y-4">
          {["seoTitle", "seoDescription", "seoTags", "seoHeading", "canonicalUrl"].map((field) => (
            <div key={field} className="space-y-1">
              <label className="block font-medium">
                {field === "canonicalUrl" ? "Canonical URL (auto)" : field.replace(/seo/i, "SEO ")}
              </label>

              {field === "seoDescription" ? (
                <textarea
                  placeholder={`Enter ${field}`}
                  className="w-full border p-3 rounded-lg"
                  value={(chapterData as any)[field] ?? ""}
                  onChange={(e) => setChapterData({ ...chapterData, [field]: e.target.value })}
                />
              ) : (
                <input
                  placeholder={`Enter ${field}`}
                  readOnly={field === "canonicalUrl"}
                  className={`w-full border p-3 rounded-lg ${
                    field === "canonicalUrl" ? "bg-gray-100" : ""
                  }`}
                  value={(chapterData as any)[field] ?? ""}
                  onChange={(e) => setChapterData({ ...chapterData, [field]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleUpdate}
          className="w-full bg-green-600 text-white p-4 rounded-lg text-xl hover:bg-green-700 transition-all"
        >
          ✅ Update Chapter
        </motion.button>
      </div>

      {/* SUCCESS MODAL */}
      {/* <AnimatePresence>
        {showPopup && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }} */}
          {/* > */}
            {/* <motion.div
              className="bg-white p-8 rounded-2xl shadow-xl text-center space-y-4 max-w-sm"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
            > */}
              {/* <h3 className="text-2xl font-bold text-green-700">Updated Successfully 🎉</h3>
              <p className="text-gray-600">
                Redirecting to updated chapter page...
              </p> */}

              {/* <button
                onClick={() =>
                  navigate(
                    `/updatechapter/${librarySlug}/${bookSlug}/${chapterData.chapterSlug}`
                  )
                }
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
              >
                Continue Editing
              </button> */}
            {/* </motion.div> */}
          {/* </motion.div>
        )}
      </AnimatePresence> */}

      <ToastContainer position="top-center" />
    </div>
  );
}
